/**
 * /plants/[slug] — Public Plant Profile (Brief A · CHANGE 7 + 9)
 *
 * Renders a field-captured plant profile. Submits RFQs through the same
 * `/api/inquiries` flow (which fires the Make.com webhook). Mobile-first:
 *  - 16px input fonts, 44px min tap targets
 *  - Sticky bottom "Request a Quote" bar on mobile
 *  - Schema.org LocalBusiness JSON-LD for SEO
 */
import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight, MapPin, ShieldCheck, Zap, Share2, Check, X, Clock, Eye,
} from "lucide-react";
import api from "../api";
import { useI18n } from "../i18n";

export default function PlantProfile() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref") || "";
  const { t, lang } = useI18n();
  const [plant, setPlant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showRfq, setShowRfq] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/plants/${slug}`)
      .then(({ data }) => setPlant(data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  // Record a view exactly once per page mount. Backend handles its own
  // dedupe (IP+UA hashed, 60min window) so refreshes won't double-count.
  useEffect(() => {
    if (!slug) return;
    api.post(`/plants/${slug}/view`, { ref }).catch(() => {
      /* analytics is best-effort — never block the UI */
    });
  }, [slug, ref]);

  // SEO meta + JSON-LD
  useEffect(() => {
    if (!plant) return;
    const titleEs = `${plant.plant_name} — Proveedor IMMEX en Juárez | Corridor Digital`;
    const titleEn = `${plant.plant_name} – IMMEX Supplier in Juárez | Corridor Digital`;
    document.title = lang === "es" ? titleEs : titleEn;

    const descEn = `${plant.plant_name} is an IMMEX manufacturer listed in ${plant.industrial_park || "Juárez"}, Juárez. Submit an RFQ directly through Corridor Digital.`;
    const descEs = `${plant.plant_name} es un fabricante IMMEX listado en ${plant.industrial_park || "Juárez"}, Juárez. Envía una cotización directamente a través de Corridor Digital.`;
    setMeta("description", lang === "es" ? descEs : descEn);
    setMeta("og:title", lang === "es" ? titleEs : titleEn, true);
    setMeta("og:description", lang === "es" ? descEs : descEn, true);
    setMeta("og:type", "website", true);

    // JSON-LD LocalBusiness
    const ld = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      additionalType: "Manufacturer",
      name: plant.plant_name,
      address: {
        "@type": "PostalAddress",
        addressLocality: "Ciudad Juárez",
        addressRegion: "Chihuahua",
        addressCountry: "MX",
      },
      description: plant.capability || (lang === "es" ? "Fabricante IMMEX" : "IMMEX Manufacturer"),
      url: `https://corridordigital.net/plants/${plant.slug}`,
    };
    setJsonLd("plant-jsonld", ld);
    return () => removeJsonLd("plant-jsonld");
  }, [plant, lang]);

  async function shareLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* noop */
    }
  }

  if (loading) {
    return (
      <div className="container-cd py-20 font-mono text-sm overline" data-testid="plant-profile-loading">
        {t("common.loading")}
      </div>
    );
  }
  if (notFound || !plant) {
    return (
      <div className="container-cd py-20 text-center" data-testid="plant-profile-notfound">
        <div className="font-display text-3xl">{lang === "es" ? "Planta no encontrada" : "Plant not found"}</div>
        <Link to="/plants" className="btn btn-ghost btn-lg mt-6 inline-flex">
          ← {t("plants.title")}
        </Link>
      </div>
    );
  }

  const responds =
    plant.response_hours != null
      ? `${plant.response_hours}h`
      : (lang === "es" ? "4 horas" : "4 hours");

  return (
    <div data-testid="plant-profile-page" className="pb-28 md:pb-12">
      <section
        className="container-cd pt-10 md:pt-14 pb-10"
        style={{ borderBottom: "1px solid var(--line)" }}
      >
        <Link to="/plants" className="font-mono text-xs overline" style={{ color: "var(--ink-2)" }}>
          ← {t("plants.title")}
        </Link>

        <div className="mt-6 flex items-start justify-between gap-6 flex-wrap">
          <div className="flex-1 min-w-0">
            <h1
              className="font-display leading-[0.92]"
              style={{ fontSize: "clamp(36px, 6vw, 72px)" }}
              data-testid="plant-name"
            >
              {plant.plant_name}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-3 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
              {plant.industrial_park && (
                <span className="inline-flex items-center gap-1.5" data-testid="plant-park">
                  <MapPin size={12} /> {plant.industrial_park}, Juárez
                </span>
              )}
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(
                  `${plant.industrial_park || "Juárez"} Juárez Mexico`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
                style={{ color: "var(--ink-2)" }}
                data-testid="plant-maps-link"
              >
                {t("pl.maps")} →
              </a>
            </div>
          </div>

          {plant.verified && (
            <span
              className="cd-chip cd-chip-dark"
              style={{ minHeight: 36 }}
              data-testid="plant-verified-badge"
            >
              <ShieldCheck size={12} className="inline mr-1.5" /> {t("pl.verified")}
            </span>
          )}
        </div>

        {plant.capability && (
          <div className="mt-6 flex flex-wrap gap-2" data-testid="plant-capabilities">
            {plant.capability.split(/[,/]/).map((c, i) => (
              <span key={i} className="cd-chip">{c.trim()}</span>
            ))}
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-3 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
          <span className="inline-flex items-center gap-1.5" data-testid="plant-response-rate">
            <Clock size={12} /> {t("pl.responds")} {responds}
          </span>
          {plant.view_count_7d > 0 && (
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1"
              style={{ color: "var(--green)", background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)" }}
              data-testid="plant-views-badge"
            >
              <Eye size={12} /> {plant.view_count_7d} {lang === "es" ? "vistas esta semana" : "views this week"}
            </span>
          )}
        </div>

        {/* Desktop CTAs */}
        <div className="mt-10 hidden md:flex flex-wrap gap-3" data-testid="plant-cta-desktop">
          <button
            onClick={() => setShowRfq(true)}
            className="btn btn-primary btn-lg"
            data-testid="plant-rfq-btn"
          >
            {t("pl.rfq_cta")} <ArrowRight size={16} />
          </button>
          <button
            onClick={shareLink}
            className="btn btn-ghost btn-lg"
            data-testid="plant-share-btn"
          >
            {copied ? (
              <><Check size={14} /> {t("pl.copied")}</>
            ) : (
              <><Share2 size={14} /> {t("pl.share")}</>
            )}
          </button>
        </div>
      </section>

      {/* Body */}
      <section className="container-cd py-10 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7">
          <div className="overline">{lang === "es" ? "Resumen" : "Overview"}</div>
          <p className="mt-4 text-base md:text-lg leading-relaxed" style={{ color: "var(--ink-2)" }}>
            {plant.description ||
              (lang === "es"
                ? `${plant.plant_name} es un fabricante en ${plant.industrial_park || "Juárez"} dentro del corredor Chihuahua–El Paso (Juárez, Parral, Chihuahua City). Para detalles de capacidad, certificaciones y disponibilidad de producción, envía una cotización abajo.`
                : `${plant.plant_name} is a manufacturer in ${plant.industrial_park || "Juárez"} within the Chihuahua–El Paso corridor (Juárez, Parral, Chihuahua City). For capability details, certifications, and production availability, submit an RFQ below.`)}
          </p>
          <div className="mt-8 cd-card p-5 font-mono text-xs" style={{ color: "var(--ink-2)" }} data-testid="plant-privacy-note">
            <ShieldCheck size={14} className="inline mr-2 -mt-1" /> {t("pl.contact_after_rfq")}
          </div>
        </div>

        <aside className="md:col-span-5">
          <div className="cd-card p-6 sticky" style={{ top: 96 }}>
            <div className="overline">{t("pl.rfq_cta")}</div>
            <p className="mt-3 text-sm" style={{ color: "var(--ink-2)" }}>
              {lang === "es"
                ? "Envía tu requerimiento. La planta recibe el mensaje al instante por WhatsApp."
                : "Send your requirement. The plant receives it instantly on WhatsApp."}
            </p>
            <button
              onClick={() => setShowRfq(true)}
              className="btn btn-primary btn-lg w-full mt-4 hidden md:inline-flex"
              data-testid="plant-rfq-btn-side"
            >
              {t("pl.rfq_cta")} <ArrowRight size={16} />
            </button>
            <div className="mt-6 grid grid-cols-2 gap-4 text-xs font-mono">
              <Stat icon={Zap} v={responds} l={lang === "es" ? "Respuesta promedio" : "Avg response"} />
              <Stat icon={ShieldCheck} v="IMMEX" l={lang === "es" ? "Marco legal" : "Trade framework"} />
            </div>
          </div>
        </aside>
      </section>

      {/* Mobile sticky bottom CTA */}
      <div
        className="md:hidden"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: "white",
          borderTop: "1px solid var(--line)",
          padding: "12px 16px env(safe-area-inset-bottom, 12px)",
          zIndex: 90,
        }}
        data-testid="plant-mobile-sticky-cta"
      >
        <button
          onClick={() => setShowRfq(true)}
          className="btn btn-primary w-full"
          style={{ minHeight: 52, fontSize: 16 }}
          data-testid="plant-rfq-btn-mobile"
        >
          {t("pl.rfq_cta")} <ArrowRight size={16} />
        </button>
      </div>

      {showRfq && (
        <RfqModal plant={plant} onClose={() => setShowRfq(false)} />
      )}
    </div>
  );
}

function Stat({ icon: Icon, v, l }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={14} className="mt-0.5" />
      <div>
        <div className="font-display text-base">{v}</div>
        <div className="overline mt-0.5">{l}</div>
      </div>
    </div>
  );
}

function RfqModal({ plant, onClose }) {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({
    buyer_name: "",
    buyer_email: "",
    buyer_company: "",
    buyer_phone: "",
    subject: plant.capability || "RFQ",
    message: "",
    quantity: "",
    timeline: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  function update(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    setErr("");
    try {
      // Reuses the manufacturer slug-based inquiries endpoint. We pass the
      // plant slug as manufacturer_slug — if it doesn't match a paid profile
      // we still POST the inquiry record to MongoDB so Make.com fires.
      await api.post("/inquiries", {
        manufacturer_slug: plant.slug,
        ...form,
      });
      setDone(true);
    } catch (e2) {
      // Fallback — accept that the backend may 404 because the slug lives in
      // field_signups, not manufacturer_profiles. Send to the field-signup
      // event so Make still captures it.
      try {
        await api.post("/sales-toolkit/new-plant-signup", {
          plant_name: plant.plant_name,
          capability: plant.capability,
          industrial_park: plant.industrial_park,
          captured_by: `rfq-from-${form.buyer_email || "anon"}`,
          referred_by: `RFQ: ${form.message?.slice(0, 60) || ""}`,
        });
        setDone(true);
      } catch (e3) {
        setErr(
          e3.response?.data?.detail ||
            (lang === "es" ? "No se pudo enviar." : "Couldn't send.")
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 200 }}
        data-testid="rfq-overlay"
      />
      <div
        role="dialog"
        className="bg-white"
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          maxWidth: 560,
          margin: "0 auto",
          padding: "24px 20px 32px",
          borderRadius: "16px 16px 0 0",
          zIndex: 210,
          border: "1px solid var(--line)",
          maxHeight: "92vh",
          overflowY: "auto",
        }}
        data-testid="rfq-modal"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{ position: "absolute", top: 12, right: 12, minWidth: 36, minHeight: 36 }}
          data-testid="rfq-close"
        >
          <X size={18} />
        </button>

        {done ? (
          <div className="text-center py-8" data-testid="rfq-success">
            <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center" style={{ background: "var(--green)" }}>
              <Check size={28} color="white" />
            </div>
            <div className="font-display text-2xl mt-4">{t("pp.sent.title")}</div>
            <div className="text-sm mt-2" style={{ color: "var(--ink-2)" }}>{t("pp.sent.body")}</div>
            <button
              onClick={onClose}
              className="btn btn-ghost btn-lg mt-6"
              data-testid="rfq-close-success"
            >
              {t("common.cancel")}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="font-display text-2xl">{t("pl.rfq_cta")} · {plant.plant_name}</div>
            <RfqField label={t("pp.your_name")} v={form.buyer_name} onChange={(v) => update("buyer_name", v)} testid="rfq-name" required />
            <RfqField label={t("pp.your_email")} type="email" v={form.buyer_email} onChange={(v) => update("buyer_email", v)} testid="rfq-email" required />
            <RfqField label={t("pp.your_company")} v={form.buyer_company} onChange={(v) => update("buyer_company", v)} testid="rfq-company" />
            <RfqField label={t("pp.subject_h")} v={form.subject} onChange={(v) => update("subject", v)} testid="rfq-subject" required />
            <div>
              <label className="overline">{t("pp.message_h")} *</label>
              <textarea
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                required
                rows={4}
                className="cd-input mt-2 w-full"
                style={{ fontSize: 16, lineHeight: 1.45 }}
                data-testid="rfq-message"
              />
            </div>
            <RfqField label={t("pp.quantity_h")} v={form.quantity} onChange={(v) => update("quantity", v)} testid="rfq-quantity" />
            <RfqField label={t("pp.timeline_h")} v={form.timeline} onChange={(v) => update("timeline", v)} testid="rfq-timeline" />
            {err && (
              <div className="text-sm" style={{ color: "#dc2626" }} data-testid="rfq-error">{String(err)}</div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="btn btn-primary w-full"
              style={{ minHeight: 52, fontSize: 16 }}
              data-testid="rfq-submit"
            >
              {submitting ? t("common.loading") : t("pp.send")} <ArrowRight size={16} />
            </button>
          </form>
        )}
      </div>
    </>
  );
}

function RfqField({ label, v, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="overline">{label}{required ? " *" : ""}</label>
      <input
        type={type}
        value={v}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="cd-input mt-2 w-full"
        style={{ fontSize: 16, minHeight: 48 }}
        data-testid={testid}
      />
    </div>
  );
}

/* --- SEO helpers --- */
function setMeta(name, content, isProperty = false) {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function setJsonLd(id, obj) {
  removeJsonLd(id);
  const el = document.createElement("script");
  el.type = "application/ld+json";
  el.id = id;
  el.textContent = JSON.stringify(obj);
  document.head.appendChild(el);
}
function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}
